import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromTerrasseBjelkelag } from "@/components/calculators/lineMappers";
import { terrasseBjelkelagCalc } from "@/domain/calculators/carpenter";

export const Route = createFileRoute("/_authenticated/calculations/carpenter/terrasse-bjelkelag")({
  component: TerrasseBjelkelagPage,
});

function TerrasseBjelkelagPage() {
  const [bredde, setBredde] = useState<number | "">("");
  const [lengde, setLengde] = useState<number | "">("");
  const [cc, setCc] = useState<number | "">(600);
  const [stolpeCc, setStolpeCc] = useState<number | "">(2.0);

  const r = useMemo(() => {
    if (bredde === "" || lengde === "" || cc === "") return null;
    return terrasseBjelkelagCalc({
      bredde_m: Number(bredde),
      lengde_m: Number(lengde),
      cc_mm: Number(cc),
      stolpe_cc_m: stolpeCc === "" ? undefined : Number(stolpeCc),
    });
  }, [bredde, lengde, cc, stolpeCc]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Terrasse bjelkelag" subtitle="Beregn bjelker, stolper og stolpesko." />
      <CalcShell
        title="Inndata"
        result={r ? (
          <>
            <ResultCard
            title="Materialbehov"
            rows={[
              { label: "Antall bjelker",  value: `${fmt(r.antall_bjelker, 0)} stk`, strong: true },
              { label: "Løpemeter bjelker", value: `${fmt(r.bjelke_lm, 1)} m` },
              { label: "Antall stolper",  value: `${fmt(r.antall_stolper, 0)} stk` },
              { label: "Antall stolpesko", value: `${fmt(r.antall_stolpesko, 0)} stk` },
            ]}
          />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Terrasse bjelkelag`} lines={linesFromTerrasseBjelkelag(r)} defaultLaborHours={Math.max(0, Math.round((r.antall_bjelker * 0.5) * 10) / 10)} />
            </div>
          </>
        ) : <div className="card p-5 text-sm text-muted-foreground">Fyll inn mål og c/c.</div>}
      >
        <Field label="Bredde (på tvers av bjelker)" unit="m"><NumberInput value={bredde} onChange={setBredde} /></Field>
        <Field label="Lengde langs bjelkene" unit="m"><NumberInput value={lengde} onChange={setLengde} /></Field>
        <Field label="c/c bjelker" unit="mm"><NumberInput value={cc} onChange={setCc} /></Field>
        <Field label="c/c stolper langs bjelke" unit="m"><NumberInput value={stolpeCc} onChange={setStolpeCc} /></Field>
      </CalcShell>
    </>
  );
}
